import { normalizeVoiceText } from "./ownerVoiceParsers";

function tokens(value = "") {
  return normalizeVoiceText(value).split(" ").filter((word) => word.length > 1);
}

function tokenDice(a = "", b = "") {
  const left = new Set(tokens(a));
  const right = new Set(tokens(b));
  if (!left.size || !right.size) return 0;
  let common = 0;
  left.forEach((word) => { if (right.has(word)) common += 1; });
  return (2 * common) / (left.size + right.size);
}

function bigrams(value = "") {
  const clean = normalizeVoiceText(value).replace(/\s+/g, " ");
  if (clean.length < 2) return clean ? [clean] : [];
  const rows = [];
  for (let index = 0; index < clean.length - 1; index += 1) rows.push(clean.slice(index, index + 2));
  return rows;
}

function charDice(a = "", b = "") {
  const left = bigrams(a);
  const right = bigrams(b);
  if (!left.length || !right.length) return 0;
  const counts = new Map();
  left.forEach((item) => counts.set(item, Number(counts.get(item) || 0) + 1));
  let common = 0;
  right.forEach((item) => {
    const count = Number(counts.get(item) || 0);
    if (count > 0) {
      common += 1;
      counts.set(item, count - 1);
    }
  });
  return (2 * common) / (left.length + right.length);
}

export function phraseSimilarity(a = "", b = "") {
  const left = normalizeVoiceText(a);
  const right = normalizeVoiceText(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  let score = 0.56 * charDice(left, right) + 0.44 * tokenDice(left, right);
  if (left.startsWith(right) || right.startsWith(left)) score = Math.max(score, 0.88);
  else if (left.includes(right) || right.includes(left)) score = Math.max(score, 0.78);
  return Math.min(1, score);
}

export function ownerRouteContext(pathname = "") {
  const clean = String(pathname || "").split("?")[0];
  const match = clean.match(/\/dashboard\/owner(?:\/([^/]+))?/);
  return match?.[1] || "hoy";
}

function lexicalScore(utterance, action = {}) {
  return [action.label, ...(action.aliases || [])]
    .filter(Boolean)
    .reduce((best, phrase) => Math.max(best, phraseSimilarity(utterance, phrase)), 0);
}

function learnedScore(utterance, intentKey, routeContext, learning = []) {
  let best = 0;
  let exactStrong = false;
  learning.forEach((row) => {
    if (row.intentKey !== intentKey) return;
    const routeMatches = row.routeContext === routeContext || row.routeContext === "owner" || routeContext === "owner";
    const routeFactor = routeMatches ? 1 : 0.72;
    const similarity = phraseSimilarity(utterance, row.utteranceKey);
    if (similarity < 0.42) return;
    const positive = Number(row.positiveCount || 0);
    const negative = Number(row.negativeCount || 0);
    const reliability = (positive + 1) / (positive + negative + 2); // Beta(1,1) posterior mean.
    const support = 1 - Math.exp(-(positive + negative) / 2.5);
    let score = similarity * reliability * (0.58 + 0.42 * support) * routeFactor;
    if (similarity === 1 && positive > 0) score = Math.max(score, 0.68 + Math.min(0.22, positive * 0.055) - Math.min(0.28, negative * 0.08));
    if (similarity === 1 && positive >= 2 && negative === 0) exactStrong = true;
    best = Math.max(best, score);
  });
  return { score: Math.max(0, Math.min(1, best)), exactStrong };
}

function contextScore(action = {}, routeContext = "") {
  const contexts = action.contexts || [];
  if (!contexts.length) return 0.2;
  if (contexts.includes(routeContext)) return 1;
  if (contexts.includes("owner")) return 0.55;
  return 0;
}

export function rankAdaptiveActions({ utterance, actions = {}, learning = [], routeContext = "owner", excludeKeys = [] }) {
  const excluded = new Set(excludeKeys);
  return Object.entries(actions)
    .filter(([key, action]) => action?.run && !excluded.has(key) && action.adaptive !== false)
    .map(([key, action]) => {
      const lexical = lexicalScore(utterance, action);
      const learned = learnedScore(utterance, key, routeContext, learning);
      const context = contextScore(action, routeContext);
      let score = 0.57 * lexical + 0.35 * learned.score + 0.08 * context;
      if (learned.exactStrong) score = Math.max(score, 0.90);
      return { key, label: action.label || key, score: Math.min(1, score), lexical, learned: learned.score, context };
    })
    .sort((a, b) => b.score - a.score || b.context - a.context || a.label.localeCompare(b.label));
}

export function adaptiveDecision(ranked = []) {
  const top = ranked[0];
  const second = ranked[1];
  if (!top) return { mode: "none", candidates: [] };
  const margin = top.score - Number(second?.score || 0);
  const canAuto = top.score >= 0.86 && margin >= 0.09 && (top.lexical >= 0.88 || top.learned >= 0.72);
  if (canAuto) return { mode: "auto", candidate: top, candidates: ranked.slice(0, 4) };
  return { mode: "ask", candidates: ranked.slice(0, 5) };
}
